/**
 * TanStack Query wrappers over the `dataProvider` seam.
 *
 * Screens use these — never `demo.ts`. Every query is `staleTime: Infinity`: the demo
 * fixtures are generated once per page load and the HTTP provider that replaces them will
 * serve slow-moving planning data, so refetching buys nothing.
 */

import { type QueryClient, useQuery } from "@tanstack/react-query";

// `dataProvider` lives in the barrel (the one-line backend swap point). It is only read
// inside query functions, i.e. long after module evaluation, so the barrel cycle is safe.
import { dataLabels, dataProvider } from "./index";
import type {
  CapacityModel,
  DataLabels,
  Lever,
  LeverFamily,
  LeverId,
  ReportId,
  ReportRow,
  Site,
  Sku,
} from "./types";

export const dataKeys = {
  skus: ["data", "skus"] as const,
  site: ["data", "site"] as const,
  capacity: ["data", "capacity"] as const,
  levers: ["data", "levers"] as const,
  leverFamilies: (leverId: LeverId) => ["data", "lever-families", leverId] as const,
  reportRows: (reportId: ReportId) => ["data", "report-rows", reportId] as const,
  labels: ["data", "labels"] as const,
};

/**
 * Warms the snapshot before the velocity screen mounts (it is one ~600KB gzip fetch plus a
 * 30k-row rehydrate, so paying for it during the login round-trip means the field paints
 * instead of spinning). Everything else off the snapshot is free once this resolves.
 */
export function prefetchFieldData(queryClient: QueryClient): void {
  void queryClient.prefetchQuery({
    queryKey: dataKeys.skus,
    queryFn: () => dataProvider.getSkus(),
    staleTime: Infinity,
  });
  void queryClient.prefetchQuery({
    queryKey: dataKeys.site,
    queryFn: () => dataProvider.getSite(),
    staleTime: Infinity,
  });
}

/** Which feeds behind the current screen are real — badge + caption copy. */
export function useDataLabels() {
  return useQuery<DataLabels>({
    queryKey: dataKeys.labels,
    queryFn: () => dataLabels(),
    staleTime: Infinity,
  });
}

/** Representative, deterministic sample of the portfolio field. */
export function useSkus() {
  return useQuery<Sku[]>({
    queryKey: dataKeys.skus,
    queryFn: () => dataProvider.getSkus(),
    staleTime: Infinity,
  });
}

/** Deck-grounded UKDC site anchors. */
export function useSite() {
  return useQuery<Site>({
    queryKey: dataKeys.site,
    queryFn: () => dataProvider.getSite(),
    staleTime: Infinity,
  });
}

/** Pick-face anchors, planning horizon and the NPI pipeline. */
export function useCapacity() {
  return useQuery<CapacityModel>({
    queryKey: dataKeys.capacity,
    queryFn: () => dataProvider.getCapacity(),
    staleTime: Infinity,
  });
}

/** The four capacity levers (relocation carries the velocity spectrum). */
export function useLevers() {
  return useQuery<Lever[]>({
    queryKey: dataKeys.levers,
    queryFn: () => dataProvider.getLevers(),
    staleTime: Infinity,
  });
}

/** Site-scale brand × pack roll-up for one lever; empty for `cap`. */
export function useLeverFamilies(leverId: LeverId) {
  return useQuery<LeverFamily[]>({
    queryKey: dataKeys.leverFamilies(leverId),
    queryFn: () => dataProvider.getLeverFamilies(leverId),
    staleTime: Infinity,
  });
}

export function useReportRows(reportId: ReportId) {
  return useQuery<ReportRow[]>({
    queryKey: dataKeys.reportRows(reportId),
    queryFn: () => dataProvider.getReportRows(reportId),
    staleTime: Infinity,
  });
}
